import * as Yup from 'yup';
import { Grid } from '@mui/material';
import { Field, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroMultiDropdown,
	CerebroMultiDropdownMenuItem,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { DataRoomFolder, DocumentType, DocumentTypeMap } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';
import { DataRoomDocument } from '../../shared/models';

interface EditDocumentFormProps {
	dataRoomGuid: string | undefined;
	document: DataRoomDocument | null | undefined;
	folder: DataRoomFolder | null | undefined;
	close: () => void;
}

export const EditDocumentForm: React.FC<EditDocumentFormProps> = ({ dataRoomGuid, document, folder, close }) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();

	const editDocumentMutation = useMutation(
		async ({ values }: EditDocumentFormMutationVariables) => {
			const documentPatch = {
				name: values.name,
				documentType: values.documentType,
				isVisibleToAll: values.isVisibleToAll,
				dataRoomUsers: values.dataRoomUsers,
				folderGuid: document?.folderGuid,
				updateAction: 'Edit',
			};

			const { data, error } = await getApiResponseData<DataRoomDocument>(
				api.patch(CerebroApiEndpoints.Documents.Update(dataRoomGuid, document?.guid), documentPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Documents.Root(dataRoomGuid, document?.folderGuid));
				snackbar.enqueueSnackbar('Data room document was edited.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={document != null} onClose={() => close()} PaperProps={{ sx: { width: '700px' } }}>
			<CerebroHeading>{document?.name}</CerebroHeading>
			<Formik
				validationSchema={EditDocumentSchema}
				initialValues={{
					name: document?.name ?? '',
					documentType: document?.documentType,
					isVisibleToAll: document?.isVisibleToAll ?? true,
					dataRoomUsers: document?.dataRoomUsers as number[],
				}}
				onSubmit={FormikUtils.MutateOnSubmit(editDocumentMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel id="name">Name</CerebroLabel>
									<Field
										id="name"
										as={CerebroInput}
										name="name"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('name', errors, touched)}
									/>
									{FormikUtils.IsInvalid('name', errors, touched) && (
										<CerebroText variant="error">{errors.name}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel required id="documentType">
										Type
									</CerebroLabel>
									<Field
										id="documentType"
										as={CerebroDropdown}
										name="documentType"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('documentType', errors, touched)}
									>
										{Object.entries(DocumentTypeMap)
											.sort(([a1, a2], [b1, b2]) => a2.localeCompare(b2))
											.map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
									</Field>
									{FormikUtils.IsInvalid('documentType', errors, touched) && (
										<CerebroText variant="error">{errors.documentType}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel id="isVisibleToAll">All Access</CerebroLabel>
									<Field
										id="isVisibleToAll"
										as={CerebroYesNo}
										name="isVisibleToAll"
										disabled={isSubmitting}
									/>
								</Grid>

								{!values.isVisibleToAll && (
									<>
										<Grid item xs={8}>
											<CerebroLabel required id="dataRoomUsers">
												Data Room Users
											</CerebroLabel>
											<Field
												id="dataRoomUsers"
												as={CerebroMultiDropdown}
												name="dataRoomUsers"
												disabled={isSubmitting}
												error={FormikUtils.IsInvalid('dataRoomUsers', errors, touched)}
											>
												{folder?.activeDataRoomUsers.map(user => (
													<CerebroMultiDropdownMenuItem key={user.guid} value={user.id}>
														{user.fullName} ({user.bankName})
													</CerebroMultiDropdownMenuItem>
												))}
											</Field>
											{FormikUtils.IsInvalid('dataRoomUsers', errors, touched) && (
												<CerebroText variant="error">{errors.dataRoomUsers}</CerebroText>
											)}
										</Grid>
									</>
								)}
								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type EditDocumentFormMutationVariables = FormikUtils.MutateOnSubmitVariables<EditDocumentFormValues>;
interface EditDocumentFormValues {
	name: string;
	documentType: DocumentType | undefined;
	isVisibleToAll: boolean;
	dataRoomUsers: number[];
}

const EditDocumentSchema = Yup.object().shape({
	name: Yup.string().required('Document name is required'),
});
